import React, { useEffect, useState } from "react";
import { CircularProgress, Switch, FormControlLabel } from "@material-ui/core";
import styled from "styled-components";
import { getFeatureFlags, updateFeatureFlag, FeatureFlag } from "../../../services/FeatureFlagService";
import { useAdminPermissions } from "../../hooks/useAdminPermissions";
import useAttachAlertDialog, { AlertType } from "../../hooks/common/useAttachAlertDialog";
import CommonAlertDialog from "../../controls/components/commonAlertDialog/CommonAlertDialog";
import { MyBookingWrapper } from "../MyBooking/styled/DesStyled";

/* ──────── styled (tiết chế: nền trung tính, tag viền, không pastel) ──────── */
const Page = styled.div`
	max-width: 820px;
	margin: 0 auto;
	background: #ffffff;
	border: 1px solid #e6e8ec;
	border-radius: 10px;
	padding: 28px 32px 12px;
`;
const Header = styled.div`
	padding-bottom: 18px;
	border-bottom: 1px solid #eef0f3;
`;
const Title = styled.h1`
	font-size: 20px;
	font-weight: 600;
	color: #1f2430;
	margin: 0 0 4px;
	letter-spacing: -0.01em;
`;
const Sub = styled.p`
	font-size: 13px;
	color: #6b7280;
	margin: 0;
	line-height: 1.55;
`;
const ViewOnly = styled.div`
	font-size: 12.5px;
	color: #6b7280;
	background: #f7f8fa;
	border: 1px solid #eef0f3;
	border-radius: 8px;
	padding: 10px 14px;
	margin-top: 18px;
`;

const Row = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 24px;
	padding: 20px 2px;
	border-bottom: 1px solid #eef0f3;
	&:last-child {
		border-bottom: none;
	}
`;
const Info = styled.div`
	flex: 1;
	min-width: 0;
`;
const NameLine = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	flex-wrap: wrap;
	font-size: 14.5px;
	font-weight: 600;
	color: #1f2430;
	margin-bottom: 4px;
`;
const Tag = styled.span<{ c: string; b: string }>`
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 0.02em;
	color: ${(p) => p.c};
	border: 1px solid ${(p) => p.b};
	border-radius: 4px;
	padding: 1px 7px;
	white-space: nowrap;
`;
const Desc = styled.div`
	font-size: 12.5px;
	color: #6b7280;
	line-height: 1.55;
	margin-bottom: 8px;
`;
const Effective = styled.div`
	display: inline-flex;
	align-items: center;
	gap: 7px;
	font-size: 12.5px;
	color: #4b5563;
`;
const Dot = styled.span<{ on: boolean }>`
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background: ${(p) => (p.on ? "#15803d" : "#b6bcc6")};
	flex-shrink: 0;
`;
const LockNote = styled.div`
	margin-top: 8px;
	font-size: 12px;
	color: #a13b3b;
	line-height: 1.5;
`;
const SwitchWrap = styled.div`
	flex-shrink: 0;
	display: flex;
	align-items: center;
	min-width: 92px;
	justify-content: flex-end;
`;
const Loading = styled.div`
	display: flex;
	justify-content: center;
	padding: 48px 0;
`;
const Empty = styled.div`
	text-align: center;
	padding: 32px 0;
	color: #9aa1ac;
	font-size: 14px;
`;

/* ──────── component ──────── */
export default function FeatureFlagsPage() {
	const { hasAny, loaded: permLoaded } = useAdminPermissions();
	const canManage = permLoaded && hasAny(["system.feature-flags.manage"]);

	const [flags, setFlags] = useState<FeatureFlag[]>([]);
	const [loading, setLoading] = useState(false);
	const [savingKey, setSavingKey] = useState<string | null>(null);

	const { dialogState, openAlertDialog } = useAttachAlertDialog({});

	useEffect(() => {
		let mounted = true;
		(async () => {
			try {
				setLoading(true);
				const data = await getFeatureFlags();
				if (!mounted) return;
				setFlags(data);
			} catch (err: any) {
				if (!mounted) return;
				openAlertDialog(
					AlertType.Fail,
					`Không tải được danh sách công tắc: ${err?.response?.data?.message || err?.message || "Vui lòng thử lại"}`,
				);
			} finally {
				if (mounted) setLoading(false);
			}
		})();
		return () => {
			mounted = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const doUpdate = async (flag: FeatureFlag, newValue: boolean) => {
		try {
			setSavingKey(flag.key);
			const result = await updateFeatureFlag(flag.key, newValue);
			setFlags((prev) => prev.map((f) => (f.key === flag.key ? result.flag : f)));
			openAlertDialog(
				AlertType.Success,
				result.message || `Đã ${newValue ? "bật" : "tắt"} "${flag.label}".`,
			);
		} catch (err: any) {
			openAlertDialog(
				AlertType.Fail,
				`Thao tác thất bại: ${err?.response?.data?.message || err?.message || "Vui lòng thử lại"}`,
			);
		} finally {
			setSavingKey(null);
		}
	};

	const handleToggleClick = (flag: FeatureFlag, newValue: boolean) => {
		if (!canManage) return;
		const actionLabel = newValue ? "bật" : "tắt";

		if (flag.risky) {
			openAlertDialog(
				AlertType.Confirm,
				`Công tắc này ảnh hưởng trực tiếp đến web khách thật và có thể tác động đến tiền. Bạn có muốn ${actionLabel} "${flag.label}" không?`,
				undefined,
				() => {
					openAlertDialog(
						AlertType.Confirm,
						`Xác nhận lần hai: thao tác ${actionLabel} "${flag.label}" sẽ thay đổi trải nghiệm web khách THẬT ngay lập tức (trừ khi đang bị chốt khẩn cấp ở máy chủ). Nhấn "Thực hiện" để tiếp tục.`,
						undefined,
						() => doUpdate(flag, newValue),
						undefined,
						{
							title: "Xác nhận lần hai — công tắc rủi ro cao",
							acceptText: "Thực hiện",
							declineText: "Hủy bỏ",
						},
					);
				},
				undefined,
				{
					title: "Cảnh báo: công tắc ảnh hưởng web khách / tiền",
					acceptText: "Tiếp tục",
					declineText: "Quay lại",
				},
			);
		} else {
			openAlertDialog(
				AlertType.Confirm,
				`Bạn có chắc muốn ${actionLabel} công tắc "${flag.label}"?`,
				undefined,
				() => doUpdate(flag, newValue),
				undefined,
				{ title: "Xác nhận thay đổi", acceptText: "Đồng ý", declineText: "Quay lại" },
			);
		}
	};

	return (
		<MyBookingWrapper>
			<Page>
				<Header>
					<Title>Công tắc hệ thống</Title>
					<Sub>
						Bật/tắt tính năng theo từng công tắc. "Đang chịu" là trạng thái thật khách
						đang trải nghiệm. Công tắc chạm web khách hoặc tiền yêu cầu xác nhận hai lần.
					</Sub>
					{!canManage && permLoaded && (
						<ViewOnly>
							Bạn đang ở chế độ chỉ xem — không thể thay đổi công tắc. Liên hệ HR để được
							cấp quyền quản lý.
						</ViewOnly>
					)}
				</Header>

				{loading ? (
					<Loading>
						<CircularProgress size={34} />
					</Loading>
				) : flags.length === 0 ? (
					<Empty>Không có công tắc nào được cấu hình.</Empty>
				) : (
					flags.map((flag) => {
						const isSaving = savingKey === flag.key;
						const checked = flag.dbEnabled ?? false;
						const disabled = !canManage || savingKey !== null;

						return (
							<Row key={flag.key}>
								<Info>
									<NameLine>
										{flag.label}
										{flag.risky && (
											<Tag c="#b45309" b="#e7cfa0">
												Rủi ro cao
											</Tag>
										)}
										{flag.locked && (
											<Tag c="#b91c1c" b="#e6bcbc">
												Đang bị chốt
											</Tag>
										)}
									</NameLine>
									{flag.desc && <Desc>{flag.desc}</Desc>}
									<Effective>
										<Dot on={flag.effective} />
										Đang chịu:&nbsp;
										<strong style={{ color: flag.effective ? "#15803d" : "#6b7280" }}>
											{flag.effective ? "Bật" : "Tắt"}
										</strong>
									</Effective>
									{flag.locked && (
										<LockNote>
											Đang bị chốt khẩn cấp ở máy chủ — nút bấm chưa có hiệu lực tức thì.
											Liên hệ kỹ thuật để gỡ chốt.
										</LockNote>
									)}
								</Info>
								<SwitchWrap>
									{isSaving ? (
										<CircularProgress size={22} style={{ margin: "0 12px" }} />
									) : (
										<FormControlLabel
											control={
												<Switch
													checked={checked}
													disabled={disabled}
													color="primary"
													onChange={() => handleToggleClick(flag, !checked)}
												/>
											}
											label={checked ? "Bật" : "Tắt"}
											labelPlacement="start"
										/>
									)}
								</SwitchWrap>
							</Row>
						);
					})
				)}

				<CommonAlertDialog {...dialogState} />
			</Page>
		</MyBookingWrapper>
	);
}
